/**
 * Formations / Courses hosts (FORM1 D1).
 *
 *   formations.neoprof.fr → French        courses.neoprof.fr → English
 *
 * One app on two hosts. Localhost and Vercel previews have no such subdomains, so they fall back
 * to path prefixes (/formations = fr, /courses = en) — the same idea as /p/:teacher for portfolios.
 * The production apex hosts never serve the prefixes: www.neoprof.fr stays free of Formations.
 */

export const LOCALE_FR = 'fr';
export const LOCALE_EN = 'en';
export const FORMATIONS_LOCALES = Object.freeze([LOCALE_FR, LOCALE_EN]);

export const HOST_LOCALE = Object.freeze({
  'formations.neoprof.fr': LOCALE_FR,
  'courses.neoprof.fr': LOCALE_EN,
});

export const LOCALE_HOST = Object.freeze({
  [LOCALE_FR]: 'formations.neoprof.fr',
  [LOCALE_EN]: 'courses.neoprof.fr',
});

export const DEV_PATH_PREFIX = Object.freeze({
  [LOCALE_FR]: '/formations',
  [LOCALE_EN]: '/courses',
});

const HOST_PORT = /:\d+$/;
const DEV_HOST = /^(localhost|127\.0\.0\.1|\[::1\]|.+\.vercel\.app)$/;
const PREFIX_ROUTE = /^\/(formations|courses)(?=\/|$)(.*)$/i;
const PREFIX_LOCALE = Object.freeze({ formations: LOCALE_FR, courses: LOCALE_EN });

const normaliseHost = (hostname) => String(hostname || '').replace(HOST_PORT, '').toLowerCase();

export const isDevFallbackHost = (hostname) => DEV_HOST.test(normaliseHost(hostname));

/**
 * Which Formations surface a request asks for.
 * Returns null — never a truthy empty object — for every other route: App.js branches on the result.
 *
 * @param {{ hostname?: string, pathname?: string }} location
 * @returns {{ locale: string, basePath: string, routePath: string }|null}
 *   basePath = '' on a production host, '/formations' or '/courses' on the dev fallback.
 */
export function resolveFormationsRoute({ hostname = '', pathname = '' } = {}) {
  const host = normaliseHost(hostname);
  const locale = HOST_LOCALE[host];
  if (locale) return { locale, basePath: '', routePath: pathname || '/' };

  if (!isDevFallbackHost(host)) return null;
  const match = PREFIX_ROUTE.exec(pathname);
  if (!match) return null;
  return {
    locale: PREFIX_LOCALE[match[1].toLowerCase()],
    basePath: `/${match[1].toLowerCase()}`,
    routePath: match[2] || '/',
  };
}

/**
 * Where the same page lives in another language.
 * Production: absolute URL on the other host. Dev fallback: the other prefix on the same origin.
 * `routePath` is already in the target locale's slugs (course slugs differ per language).
 */
export function alternateUrl(locale, routePath, { hostname = '' } = {}) {
  const path = routePath.startsWith('/') ? routePath : `/${routePath}`;
  if (isDevFallbackHost(hostname)) return `${DEV_PATH_PREFIX[locale]}${path === '/' ? '' : path}`;
  return `https://${LOCALE_HOST[locale]}${path}`;
}

/** Path to push inside the app, honouring the dev prefix. */
export const withBasePath = (basePath, routePath) => {
  const path = routePath.startsWith('/') ? routePath : `/${routePath}`;
  return `${basePath}${path === '/' && basePath ? '' : path}` || '/';
};
